"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Twitter, Linkedin, Github, Loader, ArrowRight } from "lucide-react";
function CompanyLinks() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="230327" className="space-y-3">
                {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-24 bg-muted/50 animate-pulse rounded" />)}
            </div>;
  }
  return <ul className="space-y-3">
            {menuItems.map((item, i) => <li key={item.id} data-index={i}>
                    <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors text-sm font-default">
                        {item.label}
                    </Link>
                </li>)}
        </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-4 border border-primary/20 rounded-2xl bg-primary/10 backdrop-blur-sm">
                <p className="text-primary-foreground text-sm font-medium font-default">{successMessage}</p>
            </div>;
  }
  const inputStyles = "border-0 border-b border-primary/40 rounded-none bg-transparent px-0 focus-visible:ring-0 focus-visible:border-ring shadow-none text-foreground placeholder:text-muted-foreground/60 transition-colors font-default";
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
                        <FormControl>
                            <Input placeholder="Your Name" className={inputStyles} {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />
            <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^\S+@\S+$/i,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormControl>
                            <Input type="email" placeholder="Email Address" className={inputStyles} {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />
            <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
                        <FormControl>
                            <textarea placeholder="What's on your mind?" className={`flex min-h-[60px] w-full resize-none text-base md:text-sm py-2 outline-none ${inputStyles}`} {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />
            
            {submitError && <p className="text-destructive text-sm font-default">{submitError}</p>}

            <Button type="submit" disabled={isSubmitting} className="w-full group mt-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-default font-semibold shadow-md">
                {isSubmitting ? <Loader className="animate-spin size-4 mr-2" /> : null}
                Pop It!
                <ArrowRight className="size-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
        </Form>;
}
export default function Footer() {
  const services = ['Sports Pop', 'Foodie Fidgets', 'Crafty Clicks', 'Movie Magic', 'Limited Drops', 'New Arrivals', 'Boutique Exclusives'];
  const socialLinks = [{
    icon: Twitter,
    href: "#"
  }, {
    icon: Github,
    href: "#"
  }, {
    icon: Linkedin,
    href: "#"
  }];
  return <section id="the-footer" className="bg-background text-foreground pt-24 pb-8 px-6 border-t border-border/40 relative overflow-hidden">
            {}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-secondary/20 blur-[120px] rounded-full pointer-events-none opacity-60" />

            <div className="max-w-7xl mx-auto relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-20">
                    
                    {}
                    <div className="space-y-6">
                        <Link to="#" className="inline-block">
                            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643032/img/convertix_logo_gold.png" alt="Petit Pop Toys" className="h-10" />
                        </Link>
                        <p className="text-muted-foreground text-sm leading-relaxed max-w-xs font-default">
                            Fidgets, but make them fashion. Collectible 3D printed treasures designed with love and a touch of Kawaii magic.
                        </p>
                        <div className="flex items-center gap-5">
                            {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Link key={i} to={social.href} className="text-primary hover:text-ring transition-colors" data-index={i}>
                                        <Icon className="size-5" />
                                    </Link>;
            })}
                        </div>
                    </div>

                    {}
                    <div>
                        <h3 className="font-default font-bold text-lg mb-6 text-foreground tracking-tight uppercase">Boutique</h3>
                        <ul className="space-y-3">
                            {services.slice(0, 6).map((service, i) => <li key={i} data-index={i}>
                                    <Link to="#" className="text-muted-foreground hover:text-primary transition-colors text-sm font-default">
                                        {service}
                                    </Link>
                                </li>)}
                        </ul>
                    </div>

                    {}
                    <div>
                        <h3 className="font-default font-bold text-lg mb-6 text-foreground tracking-tight uppercase">Help Center</h3>
                        <MenuProvider menu_id="27">
                            <CompanyLinks />
                        </MenuProvider>
                    </div>

                    {}
                    <div>
                        <h3 className="font-default font-bold text-lg mb-2 text-foreground tracking-tight uppercase">Pop Mail</h3>
                        <p className="text-muted-foreground text-sm mb-6 font-default">Tiny toys, big serotonin. Join our inner circle for limited drops.</p>
                        
                        <div className="p-6 rounded-3xl bg-card/60 border border-primary/20 shadow-xl backdrop-blur-md relative">
                            {}
                            <div className="absolute inset-0 border border-white/40 rounded-3xl pointer-events-none opacity-50" />
                            
                            <FormProvider formKey="newsletter_signup_petit_pop_home" formId="newsletter_signup_petit_pop_home" sectionName="footer" defaultValues={{
              name: "",
              email: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true
              }
            }} submitText="Pop It!" successMessage="You're in! Get ready for something sweet.">
                                <NewsletterForm />
                            </FormProvider>
                        </div>
                    </div>
                </div>

                <Separator className="bg-border/40 mb-8" />

                <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground font-default">
                    <p>Made with love in Quebec  © {new Date().getFullYear()} Petit Pop Toys.</p>
                    <div className="flex gap-6">
                        <Link to="#" className="hover:text-primary transition-colors">Privacy Policy</Link>
                        <Link to="#" className="hover:text-primary transition-colors">Terms of Service</Link>
                    </div>
                </div>
            </div>
        </section>;
}